import { io } from "socket.io-client";

/**
 * 앱 전역 Socket.IO 클라이언트 (백엔드 /chat 네임스페이스 — 채팅 게이트웨이가
 * 유저룸(user:{id}) 기반 개인 이벤트도 함께 발송: quote-received, quote-unread-changed, notification 등).
 *  - 접속 주소: API base 가 절대주소면 그 origin(개발 localhost:3010), 아니면 same-origin(운영 nginx 프록시).
 *  - 인증: 핸드셰이크 auth.token (재접속 시마다 localStorage 에서 최신 토큰 재조회).
 *  - 미로그인이면 접속하지 않음 — 구독자는 REST 폴백(포커스 갱신)에 의존.
 */
let socket = null;

function tokenOf() {
  try {
    const s = localStorage.getItem("muyeonAuth");
    return s ? JSON.parse(s)?.accessToken : null;
  } catch {
    return null;
  }
}
export function getAppSocket() {
  if (!tokenOf()) return null;
  if (socket) return socket;
  const base = process.env.REACT_APP_API_BASE_URL || "";
  const origin = /^https?:\/\//i.test(base) ? new URL(base).origin : window.location.origin;
  socket = io(`${origin}/chat`, {
    transports: ["websocket"],
    auth: (cb) => cb({ token: tokenOf() }),
    reconnectionDelay: 3000,
  });
  // ★ 2026-10-30 강제 로그아웃(한 기기만 로그인·계정 정지) — 서버는 ack 를 받고 소켓을 끊는다.
  //  newSessionId 가 내 세션이면 방금 내가 로그인하며 보낸 신호라 무시한다.
  //  ⚠️ 서버는 로그인 응답을 보내기 '전에' 신호를 보낸다 — 같은 브라우저의 옛 소켓(만료 토큰)이 받으면
  //   새 세션이 아직 저장 전이라 비교가 틀린다. 1.5초 기다려 저장된 값으로 다시 비교하고, 로그아웃 상태면 할 일이 없다.
  socket.on("force-logout", (payload, ack) => {
    try { if (typeof ack === "function") ack(true); } catch {}
    setTimeout(() => {
      if (!tokenOf()) return;
      if (payload?.newSessionId && payload.newSessionId === sessionIdOf()) return;
      import("common/forceLogout").then((m) => m.forceLogout({ code: payload?.code, message: payload?.message }));
    }, 1500);
  });
  return socket;
}

/** 로그아웃·강제 로그아웃 때 끊는다 — 끊지 않으면 다음 로그인 전까지 옛 회원 방(user:id)에 남는다. */
export function disconnectAppSocket() {
  if (!socket) return;
  try { socket.disconnect(); } catch {}
  socket = null;
}

function sessionIdOf() {
  try {
    const s = localStorage.getItem("muyeonAuth");
    return s ? JSON.parse(s)?.sessionId : null;
  } catch {
    return null;
  }
}

// 받은견적 배지 변화 구독(견적 도착/읽음 처리) — 반환 함수로 해제.
export function onQuoteBadgeChange(handler) {
  const s = getAppSocket();
  if (!s) return () => {};
  s.on("quote-received", handler);
  s.on("quote-unread-changed", handler);
  return () => {
    s.off("quote-received", handler);
    s.off("quote-unread-changed", handler);
  };
}

// ★ 2026-10-31 뱃지 묶음 구독(서버 'badges' — 알림·견적·채팅 안 읽음 + 분류별 알림·유형별 분류). 반환 함수로 해제.
export function onBadges(handler) {
  const s = getAppSocket();
  if (!s) return () => {};
  s.on("badges", handler);
  return () => s.off("badges", handler);
}

/* ───────── 채팅(웹) ─────────
   서버 규약: chat.gateway.ts
     보내기 join-room / leave-room / send-message / mark-read / typing
     받기   new-message / message-updated / message-reaction / messages-read / user-typing
            chat-room-added(방 요약 upsert) / room-updated(목록 한 줄 패치용 미리보기)
   서버 변경(2026-10-09, backend baa3029): clientMsgId·ack, 메시지 seq, room-updated 페이로드,
   mark-read 300ms 묶음, typing 은 join 한 방만·isTyping 필수.
   앱은 네이티브 채팅을 쓰고, 이 헬퍼는 웹 화면(/chat)이 사용한다. */

/** 방 입장 — 서버가 읽음 처리까지 수행한다. */
export function joinChatRoom(roomId) {
  getAppSocket()?.emit("join-room", { roomId: Number(roomId) });
}

export function leaveChatRoom(roomId) {
  getAppSocket()?.emit("leave-room", { roomId: Number(roomId) });
}

/** 메시지 재전송 식별자(UUID v4, 소문자). 같은 값으로 다시 보내면 서버가 처음 저장한 메시지를 돌려준다.
 *  crypto.randomUUID 는 보안 컨텍스트(https·localhost)에서만 제공되므로 getRandomValues 로 대체 생성한다. */
export function newClientMsgId() {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID().toLowerCase();
    }
  } catch { /* 아래 대체 생성 */ }
  const b = new Uint8Array(16);
  if (typeof crypto !== "undefined" && crypto.getRandomValues) crypto.getRandomValues(b);
  else for (let i = 0; i < 16; i++) b[i] = Math.floor(Math.random() * 256);
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const h = Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("");
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

/** 전송 응답 대기 시간(ms). 초과하면 실패로 표시하고, 재시도는 같은 clientMsgId 로 보낸다. */
const SEND_ACK_TIMEOUT_MS = 15000;

/**
 * 메시지 전송. type 은 TEXT·IMAGE·VIDEO 만 허용(서버 검증). 사진은 type=IMAGE + imageUrl.
 * 서버 응답(ack)을 Promise 로 돌려준다 — 항상 resolve 한다.
 *   성공 { ok:true, message } · 재전송 { ok:true, duplicate:true, message } · 실패 { ok:false, code }
 *   소켓 없음 → code NO_SOCKET, 응답 시간 초과 → code TIMEOUT.
 */
export function sendChatMessage({ roomId, content = "", type = "TEXT", imageUrl, replyToId, clientMsgId }) {
  const s = getAppSocket();
  if (!s) return Promise.resolve({ ok: false, code: "NO_SOCKET" });
  if (!s.connected) s.connect();
  return s.timeout(SEND_ACK_TIMEOUT_MS)
    .emitWithAck("send-message", {
      roomId: Number(roomId), type, content, imageUrl, replyToId, clientMsgId,
    })
    .then((ack) => (ack && typeof ack === "object" ? ack : { ok: false, code: "SEND_FAILED" }))
    .catch(() => ({ ok: false, code: "TIMEOUT" }));
}

/** 내가 보낸 메시지 삭제. 서버 확인 응답이 없으면 실패로 처리한다. */
export function deleteChatMessage(roomId, messageId) {
  const s = getAppSocket();
  if (!s?.connected) return Promise.reject(new Error("socket disconnected"));
  return new Promise((resolve, reject) => {
    s.timeout(5000).emit("delete-message", {
      roomId: Number(roomId), messageId: Number(messageId),
    }, (err, result) => {
      if (err || !result?.message) reject(err || new Error("delete failed"));
      else resolve(result.message);
    });
  });
}

/**
 * 내가 보낸 글 메시지 수정(edit-message). 성공 { ok: true, message } — 방에는 서버가 message-updated 를 보낸다.
 *  서버가 거절하면(작성자 아님·삭제됨 등) 확인 응답 없이 예외만 나므로 시간 초과로 실패 처리한다.
 *  돌려주는 값: { ok: true, message } | { ok: false, code }
 */
export function editChatMessage(roomId, messageId, content) {
  const s = getAppSocket();
  if (!s) return Promise.resolve({ ok: false, code: "NO_SOCKET" });
  if (!s.connected) s.connect();
  return s.timeout(8000)
    .emitWithAck("edit-message", { roomId: Number(roomId), messageId: Number(messageId), content })
    .then((ack) => (ack && typeof ack === "object" ? ack : { ok: false, code: "EDIT_FAILED" }))
    .catch(() => ({ ok: false, code: "TIMEOUT" }));
}

export function markChatRead(roomId) {
  getAppSocket()?.emit("mark-read", { roomId: Number(roomId) });
}

/** 입력 중 신호. isTyping 은 필수(true=입력 중, false=멈춤). 서버는 join 한 방만 중계한다. */
export function sendTyping(roomId, isTyping) {
  getAppSocket()?.emit("typing", { roomId: Number(roomId), isTyping: !!isTyping });
}

/**
 * 방 안에서 쓰는 구독 묶음 — 반환 함수로 일괄 해제.
 * 방 입장(join-room)도 여기서 한다: 연결돼 있으면 즉시 1회, 이후에는 재연결(connect) 때만 다시 보낸다.
 * onReconnect 는 재연결 직후 호출된다(끊긴 동안 놓친 메시지 채우기용).
 */
export function onChatRoomEvents(roomId, { onMessage, onUpdated, onDeleted, onRead, onTyping, onReaction, onReconnect } = {}) {
  const s = getAppSocket();
  if (!s) return () => {};
  const rid = Number(roomId);
  const same = (p) => Number(p?.roomId) === rid;
  // 네트워크 전환·서버 재시작 뒤 Socket.IO는 연결만 복구하고 room:{id} 가입은
  // 서버에서 사라진다. 재연결 때마다 현재 방에 다시 들어가야 실시간 수신이 이어진다.
  const rejoin = () => s.emit("join-room", { roomId: rid });
  const onConnect = () => { rejoin(); onReconnect?.(); };
  const h = {
    "new-message": (p) => same(p) && onMessage?.(p.message),
    "message-updated": (p) => same(p) && onUpdated?.(p.message),
    "message-deleted": (p) => same(p) && onDeleted?.(p.message),
    "messages-read": (p) => same(p) && onRead?.(p),
    "user-typing": (p) => same(p) && onTyping?.(p),
    // 이모지 반응 변경 신호 { roomId, messageId } — 반응 목록은 보는 사람마다 mine 이 달라 다시 받아야 한다.
    "message-reaction": (p) => same(p) && onReaction?.(p),
  };
  Object.entries(h).forEach(([e, fn]) => s.on(e, fn));
  s.on("connect", onConnect);
  if (s.connected) rejoin();
  else s.connect();
  return () => {
    Object.entries(h).forEach(([e, fn]) => s.off(e, fn));
    s.off("connect", onConnect);
  };
}

/**
 * 목록·배지용 — 방 추가/갱신 신호.
 *  onAdded(summary)   chat-room-added: 방 요약 전체(목록 행과 같은 형태) → upsert
 *  onUpdated(payload) room-updated: { roomId, lastMessage, lastMessageAt, lastSeq, lastMessageType,
 *                     senderId, senderNickname, unreadDelta, unreadCount } (방마다 1초 묶음).
 *                     읽음 처리 후에는 { roomId, unreadCount: 0, lastSeq }. 구 서버는 roomId 만 보낸다.
 *  onReconnect()      재연결 직후(끊긴 동안 놓친 신호 보정용)
 */
export function onChatRoomListChange({ onAdded, onUpdated, onReconnect } = {}) {
  const s = getAppSocket();
  if (!s) return () => {};
  const added = (p) => onAdded?.(p);
  const updated = (p) => onUpdated?.(p);
  // 첫 연결은 화면이 이미 REST 로 받았으므로 제외하고, 끊겼다 다시 붙을 때만 알린다.
  let wasConnected = s.connected;
  const connected = () => { if (wasConnected) onReconnect?.(); wasConnected = true; };
  s.on("chat-room-added", added);
  s.on("room-updated", updated);
  s.on("connect", connected);
  return () => {
    s.off("chat-room-added", added);
    s.off("room-updated", updated);
    s.off("connect", connected);
  };
}
